import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "../../components/dashboard/DashboardLayout";
import { PageHeader } from "../../components/dashboard/UI";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";
import {
  Download, ShieldCheck, Server, Zap, Cpu, Activity, FileText,
  FileLock2, FileBadge, FileCog, BookOpen,
} from "lucide-react";

const SYGNAL_LOGO_WHITE =
  "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/jp3l3gtk_Sygnal%20Shiel%20Logo%20%282%29.jpeg";

const CATEGORY_META = {
  FARADAY:  { icon: ShieldCheck, color: "#19A7E0" },
  TEMPEST:  { icon: Server,      color: "#19A7E0" },
  HEMP:     { icon: Zap,         color: "#FF6A3D" },
  SPECTRUM: { icon: Activity,    color: "#0E9F6E" },
  TACTICAL: { icon: Cpu,         color: "#19A7E0" },
};

const FILE_ICON = {
  datasheet: FileText,
  whitepaper: BookOpen,
  compliance: FileBadge,
  firmware_notes: FileCog,
};

const FILTERS = [
  { key: "all",      labelKey: "vault.filterAll" },
  { key: "FARADAY",  labelKey: "vault.filterFaraday" },
  { key: "TEMPEST",  labelKey: "vault.filterTempest" },
  { key: "HEMP",     labelKey: "vault.filterHemp" },
  { key: "SPECTRUM", labelKey: "vault.filterSpectrum" },
  { key: "TACTICAL", labelKey: "vault.filterTactical" },
];

const SygnalVault = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/sygnal-vault")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.category === filter);
  }, [items, filter]);

  return (
    <DashboardLayout>
      {/* Brand banner */}
      <div
        className="mb-6 -mx-6 lg:-mx-12 px-6 lg:px-12 py-6 border-b border-white/10 bg-black flex items-center gap-5"
        data-testid="vault-brand-banner"
      >
        <img
          src={SYGNAL_LOGO_WHITE}
          alt="Sygnal Shield"
          className="h-12 w-auto select-none"
          draggable={false}
          data-testid="vault-sygnal-logo"
        />
        <div className="h-10 w-px bg-white/15" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
            ◉ AUTHORIZED · CLIENT-LEVEL ACCESS
          </p>
          <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
            Proprietary Sygnal Shield documentation · Do not redistribute
          </p>
        </div>
      </div>

      <PageHeader
        eyebrow={t("vault.eyebrow")}
        title={t("vault.title")}
        subtitle={t("vault.subtitle")}
      />

      {/* Filters */}
      <div className="mb-8 flex flex-wrap gap-2" data-testid="vault-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`vault-filter-${f.key}`}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm border transition-colors ${
              filter === f.key
                ? "bg-[#19A7E0]/10 border-[#19A7E0]/40 text-[#19A7E0]"
                : "bg-transparent border-white/10 text-white/60 hover:border-white/30 hover:text-white"
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {/* Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((it) => {
          const meta = CATEGORY_META[it.category] || { icon: ShieldCheck, color: "#19A7E0" };
          const FileIcon = FILE_ICON[it.file_type] || FileText;
          const Icon = meta.icon;
          return (
            <article
              key={it.id}
              data-testid={`vault-item-${it.id}`}
              className="card-sharp p-6 hover:bg-[#0A0D14] transition-colors"
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 flex-shrink-0 border rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${meta.color}1A`, borderColor: `${meta.color}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: meta.color }}>
                    {it.category} · {it.file_type.replace("_", " ")}
                  </p>
                  <h3 className="mt-2 text-base font-medium tracking-tight">{it.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{it.summary}</p>
                </div>
                <button
                  onClick={(e) => e.preventDefault()}
                  data-testid={`vault-download-${it.id}`}
                  className="btn-ghost px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] rounded-sm inline-flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
                <Meta label={t("vault.attenuation")} value={it.attenuation_db ? `${it.attenuation_db} dB` : "—"} />
                <Meta label={t("vault.version")} value={it.version || "—"} />
                <Meta label={t("dashboard.size")} value={`${(it.size_kb / 1024).toFixed(1)} MB`} />
              </div>

              {it.standards && it.standards.length > 0 && (
                <div className="mt-4">
                  <p className="tactical-label text-[10px]">{t("vault.standards")}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {it.standards.map((s) => (
                      <span
                        key={s}
                        className="px-2 py-1 bg-white/[0.04] border border-white/10 rounded-sm font-mono text-[10px] text-white/70"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center gap-2">
                <FileIcon className="w-3.5 h-3.5 text-white/40" />
                <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.2em]">
                  {t("dashboard.uploaded")}: {new Date(it.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="card-sharp p-10 text-center" data-testid="vault-empty">
          <FileLock2 className="w-8 h-8 text-white/30 mx-auto" />
          <p className="mt-4 text-sm text-white/60">{t("vault.noItems")}</p>
        </div>
      )}
    </DashboardLayout>
  );
};

const Meta = ({ label, value }) => (
  <div className="bg-[#050505] p-3">
    <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">{label}</p>
    <p className="mt-1 text-sm font-medium">{value}</p>
  </div>
);

export default SygnalVault;
